import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircle,
  AlertCircle,
  Loader,
  Zap,
  Database,
  Star,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { DashboardRecentActivity, ApplicationStatus } from '../types';

interface RecentActivityTimelineProps {
  activities: DashboardRecentActivity[];
}

const statusColors: Record<ApplicationStatus, string> = {
  received: 'bg-blue-100 text-blue-800 border-blue-200',
  enriching: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  enriched: 'bg-green-100 text-green-800 border-green-200',
  ai_processing: 'bg-purple-100 text-purple-800 border-purple-200',
  ai_complete: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  synced: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  stub: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  skipped: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
};

const statusIcons: Record<ApplicationStatus, React.ElementType> = {
  received: Database,
  enriching: Loader,
  enriched: CheckCircle,
  ai_processing: Zap,
  ai_complete: CheckCircle,
  synced: CheckCircle,
  error: AlertCircle,
  stub: Database,
  skipped: AlertCircle,
};

const statusLabels: Record<ApplicationStatus, string> = {
  received: 'Received',
  enriching: 'Enriching',
  enriched: 'Enriched',
  ai_processing: 'AI Processing',
  ai_complete: 'AI Complete',
  synced: 'Synced',
  error: 'Error',
  stub: 'Stub',
  skipped: 'Skipped',
};

const recommendationLabels: Record<number, string> = {
  1: 'Strong No',
  2: 'No',
  3: 'Maybe',
  4: 'Yes',
  5: 'Strong Yes',
};

export default function RecentActivityTimeline({
  activities,
}: RecentActivityTimelineProps) {
  if (activities.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
        No recent activity
      </div>
    );
  }

  const displayActivities = activities.slice(0, 10);

  return (
    <div className="flow-root">
      <ul role="list" className="-mb-8">
        {displayActivities.map((activity, index) => {
          const status = activity.application.status.toLowerCase() as ApplicationStatus;
          const Icon = statusIcons[status] || Database;
          const isLast = index === displayActivities.length - 1;

          return (
            <li key={activity.application.id}>
              <div className="relative pb-8">
                {!isLast && (
                  <span
                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        status === 'error'
                          ? 'bg-red-100'
                          : status === 'synced'
                            ? 'bg-emerald-100'
                            : 'bg-gray-100 dark:bg-gray-700'
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          status === 'error'
                            ? 'text-red-600'
                            : status === 'synced'
                              ? 'text-emerald-600'
                              : 'text-gray-600 dark:text-gray-400'
                        } ${status === 'enriching' || status === 'ai_processing' ? 'animate-spin' : ''}`}
                      />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {activity.candidate ? (
                          <Link
                            to={`/candidates/${activity.candidate.id}`}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            {activity.candidate.firstName} {activity.candidate.lastName}
                          </Link>
                        ) : (
                          <span className="text-gray-900 dark:text-gray-100">Candidate Name Loading...</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {activity.job?.title || 'Unknown Job'}
                      </p>
                      {activity.review && (
                        <div className="mt-1 flex items-center gap-2">
                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Score: {(activity.review.score / 10).toFixed(1)}/5 - {recommendationLabels[activity.review.recommendation] || 'N/A'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                          statusColors[status]
                        }`}
                      >
                        {statusLabels[status]}
                      </span>
                      <p className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                        {formatDistanceToNow(new Date(activity.application.updatedAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
